<template>
  <DsModal
    :open="true"
    :title="$t('components.PublicAccessModal.label.title')"
    :primary-button-label="$t('components.PublicAccessModal.label.button.confirmButton')"
    :secondary-button-label="$t('components.PublicAccessModal.label.button.cancelButton')"
    class="public-access-modal"
    height="284px"
    width="600px"
    @secondary="cancelAction()"
    @primary="confirmAction()"
  >
    <template slot="content">
      <section class="public-access-modal">
        <div class="public-access-modal__email">
          <v-text-field
            v-model="email"
            outlined
            :rules="emailRules"
            :label="$t('components.PublicAccessModal.label.form.title')"
            type="text"
          />
        </div>

        <div class="public-access-modal__agree-terms">
          <v-checkbox
            v-model="agreeTerms"
            class="public-access-modal__agree-terms__checkbox"
          />

          <span class="public-access-modal__agree-terms--text">
            {{ $t('components.PublicAccessModal.label.form.agreeTerms.messageFirstPart') }}

            <a
              href="https://stilingue.com.br/termo-de-uso-do-dashboards-by-studio-stilingue"
              target="_blank"
              @click.stop
            >{{ $t('components.PublicAccessModal.label.form.agreeTerms.useTerms') }}</a>

            {{ $t('components.PublicAccessModal.label.form.agreeTerms.messageSecondPart') }}

            <a
              :href="privacyPolicyLink"
              target="_blank"
              @click.stop
            >{{ $t('components.PublicAccessModal.label.form.agreeTerms.privacyPolicy') }}</a>.
          </span>
        </div>
      </section>
    </template>
  </DsModal>
</template>

<script>
import { DsModal } from '@ds/components';
import { mapActions, mapGetters } from 'vuex';
import Cookies from 'js-cookie';
import { Flags } from '@/plugins/rollout';
import { splitStringOnSeparator } from '@/utils/formatter';

export default {
  name: 'PublicAccessModal',
  components: {
    DsModal,
  },
  data() {
    return {
      valid: false,
      agreeTerms: false,
      showContent: false,
      email: '',
      emailRules: [
        v => (v && Boolean(v)) || this.$t('components.PublicAccessModal.label.form.error.addEmail'),
      ],
      privacyPolicyLink: '',
      publicUserCookieKey: 'public-user-logged-in',
    };
  },
  computed: {
    ...mapGetters('featureFlag', [
      'getValue',
    ]),
  },
  watch: {
    agreeTerms() {
      this.validateForm();
    },
    email() {
      this.validateForm();
    },
  },
  async mounted() {
    this.changeConfirmButtonValue();
    this.toggleScroll();

    const publicDashboardInfo = await this.getValue(Flags.publicDashboardInfo);
    this.privacyPolicyLink = splitStringOnSeparator(publicDashboardInfo, '|')[1] || '';
  },
  destroyed() {
    this.toggleScroll();
  },
  methods: {
    ...mapActions('dashboard', [
      'savePublicUser',
      'getPublicUser',
    ]),
    cancelAction() {
      location.href = process.env.VUE_APP_WARROOM_URL;
    },
    confirmAction() {
      this.savePublicUser({ email: this.email })
        .then(() => {
          Cookies.set(this.publicUserCookieKey, true, {
            sameSite: 'None', secure: true,
          });
        });

      this.showContent = true;
      this.$emit('confirm', { value: false });
    },
    validateForm() {
      this.valid = Boolean(this.agreeTerms && this.email);

      this.changeConfirmButtonValue();
    },
    changeConfirmButtonValue() {
      const primaryButton = document.getElementsByClassName('ds-button ds-button--primary ds-button--default')[0];
      primaryButton.disabled = !this.valid;
    },
    toggleScroll() {
      const vMainWrap = document.getElementsByClassName('v-main__wrap')[0];
      vMainWrap.classList.toggle('disable-scroll', !this.showContent);
    },
  },
};
</script>

<style lang="scss" scoped>
  .public-access-modal {
    z-index: var(--zindex-10);
    backdrop-filter: blur(16px);
    width: 100%;
    height: 100%;

    &__email {
      padding-top: var(--ds-spacing-stack-nano);
    }

    &__agree-terms {
      display: flex;
      margin-top: 20px;

      a {
        grid-area: link;
        align-self: center;
        text-decoration: var(--ds-text-transform-underline);
        color: var(--ds-color-text-interactive);
        cursor: pointer;
        padding: 0 0 var(--ds-spacing-stack-nano) 0;
        max-width: fit-content;

        &:hover {
          color: var(--ds-color-text-interactive);
        }
      }

      &__checkbox {
        position: relative;
        top: -32px;
      }

      &--text {
        font-size: var(--ds-font-size-sm);
        font-weight: var(--ds-font-weight-semibold);
        line-height: 20px;
        display: inline-block;
        align-items: end;
      }
    }
  }

  ::v-deep {
    .ds-modal__overlay {
      backdrop-filter: blur(10px);
    }

    @media (max-width: 768px) {
      .ds-modal__component {
        height: 38vh !important;
        width: 90vw !important;
      }
    }

    @media (max-height: 700px), (max-width: 320px) {
      .ds-modal__component {
        height: 45vh !important;
        width: 95vw !important;
      }
    }

    .ds-modal__component {
      background-color: var(--ds-color-surface-contrast);
    }

    .ds-modal__header {
      padding-bottom: 0;
    }

    .ds-modal__content,
    .ds-modal__slot-content {
      margin-top: 0;
    }

    .ds-modal__container {
      padding-bottom: 0;
      overflow-y: hidden !important;
      height: 100%;
    }

    .v-input__slot {
      height: 40px;
      margin: 2px 2px 2px 0;
    }

    .v-text-field {
      &__slot {
        & textarea {
          resize: none;
          line-height: 24px;
          margin: var(--ds-spacing-stack-nano) 0 !important;
        }

        & label:nth-child(1) {
          top: 6px;
        }
      }

      &__details {
        padding: 0 !important;
        margin: 0 !important;
      }

      & .v-input__control .v-input__slot {
        min-height: auto !important;
        display: flex !important;
        align-items: center !important;
      }
    }

    .public-access-modal {
      & .v-input--has-state.error--text .v-label {
        top: 6px;
      }

      & .v-label--active:first-child {
        transform: translateY(-20px) scale(1) !important;
        font-size: var(--ds-font-size-xs) !important;
        background-color: inherit;
      }
    }
  }
</style>
